import {IconButton} from '@wandb/components';
import React, {useCallback, useRef} from 'react';

import * as Analytics from '../../services/analytics/productEvents';
import {DD_PRIVACY_HIDDEN} from '../../util/datadog/privacy';
import {reportFullscreenReadyTiming} from './timing';
import {type PointCloudViewerProps} from './types';
import {usePointCloudCardScreenshot} from './usePointCloudCardScreenshot';

/**
 * Shared point-cloud card + fullscreen shell. Library-specific drawing comes
 * from `renderer` (Babylon or Potree). Timing and existing product events are
 * owned here so both libraries share one tracking surface.
 */
export const PointCloudViewer = ({
  scene,
  width,
  height,
  renderer,
  sourcePointCount,
  backgroundColor,
  pointColor,
  useRightHandedSystem,
  cameraControl,
  onTiming,
  trackSuccess,
  trackError,
}: PointCloudViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {screenshot, renderErrorMessage} = usePointCloudCardScreenshot({
    scene,
    width,
    height,
    renderer,
    sourcePointCount,
    backgroundColor,
    pointColor,
    useRightHandedSystem,
    cameraControl,
    onTiming,
    trackSuccess,
    trackError,
  });

  const requestFullscreen = useCallback(() => {
    let cleanup: (() => void) | undefined;
    const mount = async () => {
      if (containerRef.current == null) {
        return;
      }
      const startedAt = performance.now();
      const result = await renderer.mountFullscreen({
        scene,
        domElement: containerRef.current,
        backgroundColor,
        pointColor,
        useRightHandedSystem,
      });
      cleanup = result.cleanup;
      reportFullscreenReadyTiming(onTiming, {
        renderer: renderer.id,
        durationMs: performance.now() - startedAt,
        sourcePointCount,
      });
      Analytics.pointCloudViewed({type: 'fullscreen'});
    };
    void mount();
    return () => cleanup?.();
  }, [
    renderer,
    scene,
    backgroundColor,
    pointColor,
    useRightHandedSystem,
    onTiming,
    sourcePointCount,
  ]);

  if (renderErrorMessage) {
    return <div className="card3d">Error: {renderErrorMessage}</div>;
  }

  return (
    <div className="media-card group">
      <div className="object3D-card-babylon" ref={containerRef} />
      {screenshot && (
        <>
          <img
            alt="point-cloud-card"
            data-test="point-cloud-card"
            data-dd-privacy={DD_PRIVACY_HIDDEN}
            src={screenshot}
          />
          {/* oxlint-disable-next-line tailwindcss/no-restricted-classes -- grandfathered :has() usage */}
          <div className="media-card__fullscreen [&:has(:focus-visible)]:!text-white [&_button]:!bg-transparent">
            <IconButton
              aria-label="Open point cloud fullscreen"
              icon="full-screen-mode-expand"
              iconColor="currentcolor"
              onPress={requestFullscreen}
              variant="ghost"
            />
          </div>
        </>
      )}
    </div>
  );
};
